import Filter from './Filter';
import FilterTools from './FilterTools';
import FilterConfig from './FilterConfig';
import FilterInputFactory from './FilterInputFactory';
import SearchPointForm from '../SearchPointForm.js';
import SelectedArea from './SelectedArea';
import AreasCreator from '../../SzumDrawer/AreasCreator.js';
import Checkbox from '../Checkbox/Checkbox';
import type { API_DataType } from '../../types-telematics';
import type { FilterMongoCondition, GeojsonType, QueryType } from '../../types';
import type { FilterSubMenuValue, RelationType } from '../../types-analytics';
import { _ } from '../../Lang.class.js';
import ErrorSupport from './ErrorSupport';
import SliderData from './../Colorizer/SliderData';
import './../Colorizer/ModalColorizerSetup.scss';
import { sliderTelematicsSingle } from '../Colorizer/sliderTelematicsSingle';
import { sliderTelematicsRange } from '../Colorizer/sliderTelematicsRange';
import { featureKeepAlignmentWithSlider } from './featureKeepAlignmentWithSlider';
import { Spinner } from '../Spinner/Spinner';
import { CalendarRange } from '../CalendarRange/CalendarRange';
import { MultiSelectWide } from '../MultiSelectWide/MultiSelectWide';
import { ResolutionConverter } from '../Colorizer/ResolutionConverter';
import { AndOrSelect } from '../AndOrSelect/AndOrSelect';

import Coordinate from 'jsts/org/locationtech/jts/geom/Coordinate.js';
import Geometry from 'jsts/org/locationtech/jts/geom/Geometry.js';
import GeometryFactory from 'jsts/org/locationtech/jts/geom/GeometryFactory.js';
import BufferOp from 'jsts/org/locationtech/jts/operation/buffer/BufferOp.js';

// import { Geometry, Coordinate, GeometryFactory } from 'jsts/org/locationtech/jts/geom';
// import BufferOp from 'jsts/org/locationtech/jts/operation/buffer/BufferOp';

import { RelativeSliderSettingsOrNot } from '../Colorizer/RelativeSliderSettingsOrNot';
import type ColorizeSlider from '../Colorizer/ColorizeSlider';
import { ResolutionToday } from '../Colorizer/ResolutionToday';
import { ResolutionDate } from '../Colorizer/ResolutionDate';
import { getValuesIncludingInfinityFromSliderOutput } from './getValuesIncludingInfinityFromSliderOutput';
import { explodeValuesFromFilterItem } from './explodeValuesFromFilterItem';
import { clickOutside } from '../../../../utilities/clickOutside';
Object.assign(Geometry.prototype, {
    buffer: function () {
        return BufferOp.bufferOp(this, ...arguments);
    },
});

interface FilterEditorItemCallbacks {
    onSave: (key: string, filterItem: QueryType, andOrSelection: string) => void;
    onRemove: (key: string) => void;
    onTemporaryHideModal: () => void;
    onChangeSubMenu: (key: string) => void;
    onShowModal: () => void;
    onDrawComplete: (key: string, geojson: GeojsonType) => void;
    onDrawCancel: (key: string) => void;
    onSaveRelatedFields: (key: string, relatedFields: RelationType) => void;
    onChangeAnything: (key: string) => void;
}

export interface FilterEditorItemType {
    label: string;
    info?: string;
    mongoCondition: { [key: string]: any | any[] };
    dataType: API_DataType;
    displayMethod: string;
    unit: string;
    availableValues: { id: string | number; name: string }[];
    maxLength: number;
    min: number | null;
    max: number | null;
    period_range?: { min: string; max: string };
    collection?: string;
    fld_period?: string;
    default_values?: (number | false)[];
}

class FilterEditorItem {
    private root: HTMLDivElement;
    private key: string;
    private isSelected: boolean;
    private isUsed: boolean;
    private filterItem: FilterEditorItemType;
    private values: string | number | (string | number)[] | any = null;
    private callbacks: FilterEditorItemCallbacks;
    private szumDrawer: any;
    private searchform: SearchPointForm;
    private isNegation: boolean;
    private subMenu: FilterSubMenuValue;
    private selectedSubMenuItemId: string;
    private canBeRemoved: boolean;
    private filter: Filter;
    private relatedFields: RelationType | null;
    private subMenuDisabled: boolean;
    private slider: sliderTelematicsRange | null;
    private sliderModeToggle: any;

    constructor(
        root: HTMLDivElement,
        filter: Filter,
        key: string,
        isSelected: boolean,
        isUsed: boolean,
        subMenu: FilterSubMenuValue,
        subMenuDisabled: boolean,
        filterItem: FilterEditorItemType,
        szumDrawer: any,
        canBeRemoved: boolean,
        callbacks: FilterEditorItemCallbacks
    ) {
        this.root = root;
        this.key = key;
        this.isSelected = isSelected;
        this.isUsed = isUsed;
        this.subMenu = subMenu;
        this.subMenuDisabled = subMenuDisabled;
        this.selectedSubMenuItemId = this.findSelectedSubMenuItemId(this.key);
        this.filterItem = this.getFilterItemDefault(filterItem);
        this.callbacks = callbacks;
        this.szumDrawer = szumDrawer;

        this.isNegation = this.isNegationInCondition(this.filterItem);
        this.canBeRemoved = canBeRemoved;

        this.relatedFields = null;

        this.filter = filter;

        // console.log('FilterEditorItem, constructor', this.key, this.filterItem, this.filter.getFilterQueries());

        this.slider = null;
        this.sliderModeToggle = null;

        if (this.isSelected) this.reload();
    }

    findSelectedSubMenuItemId(key: string): string {
        let selectedSubMenuItemId = key;
        if (this.subMenu.items.length > 0) {
            // Find id in subMenu otherwise take first
            selectedSubMenuItemId =
                this.subMenu.items.find(item => item.id === key)?.id ?? this.subMenu.items[0].id;
        }
        return selectedSubMenuItemId;
    }

    getFilterItemDefault(filterItem: FilterEditorItemType): FilterEditorItemType {
        return {
            label: filterItem.label ?? 'Brak etykiety',
            info: filterItem.info ?? '',
            mongoCondition: filterItem.mongoCondition ?? {},
            dataType: filterItem.dataType ?? 'string',
            displayMethod: filterItem.displayMethod ?? FilterConfig.displayMethod.SINGLE,
            unit: filterItem.unit ?? '',
            availableValues: filterItem.availableValues ?? [],
            maxLength: filterItem.maxLength ?? 0,
            min: filterItem.min ?? null,
            max: filterItem.max ?? null,
            period_range: filterItem.period_range ?? null,
            collection: filterItem.collection ?? null,
            fld_period: filterItem.fld_period ?? null,
            default_values: filterItem.default_values, // ?? [-10, -10],
        };
    }

    load(filter: Filter) {
        this.filterItem.mongoCondition = filter.getFilterItem(this.key.split('/')[0]);
        // console.log('@emptyField FilterEditorItem, load', this.key, this.filterItem, this.filter.getFilterQueries());
        if (this.key == 'geom/area' && this.filterItem.mongoCondition) {
            SelectedArea.setGeometry(this.filterItem.mongoCondition);
            // SelectedArea.drawArea();
        }
    }

    reload() {
        console.log(
            'FilterEditorItem, reload',
            this.key,
            this.filterItem,
            this.subMenu,
            this.filter
        );

        let help = '';
        if (this.subMenu.items.length) {
            for (let i = 0; i < this.subMenu.items.length; i++) {
                if (this.subMenu.items[i].id == this.key) {
                    if (this.subMenu.items[i].info) {
                        help = this.subMenu.items[i].info;
                    }
                    break;
                }
            }
            if (!help && this.subMenu.info) {
                help = this.subMenu.info;
            }
        } else if (this.filterItem.info) {
            help = this.filterItem.info;
        }

        //  ${(typeof this.filterItem.unit !== 'undefined' && this.filterItem.unit.trim()!=='' ? `[${this.filterItem.unit}]` : ``)}

        this.root.innerHTML = `
                <div class="filter-label">
                    <div>${_(this.filterItem.label)}</div>
                    <div>${help ? '<span class="filter-description-button"></span>' : ''}</div>
                </div>
                <div class="filter-values-container">
                    <div class="filter-merged-fields"></div>
                    <div class="filter-values"></div>
                </div>
                ${help ? '<div class="filter-description">' + help.replace(/\n/g, '<br/>') + '</div>' : ''}
                <div style="display: flex; flex-direction: column;">
                    <div>
                        <div class="js-and-or-select"></div>
                    </div>
                    <div  class="filter-buttons">
                        <button class="button-filter js-remove button-remove" style="display: ${this.canBeRemoved ? 'block' : 'none'}">${_('Usuń filtr')}</button>
                        <button class="button-filter js-filter-save button-save">${_('Zapisz filtr')}</button>
                    </div>
                </div>
        `;

        let btn_save = this.root.querySelector('.js-filter-save') as HTMLButtonElement;
        let btn_remove = this.root.querySelector('.js-remove') as HTMLButtonElement;
        let btn_show_desc = this.root.querySelector(
            '.filter-description-button'
        ) as HTMLSpanElement;

        let addToggleSliderModeInside = this.subMenu.items.length === 0; // FIXME: set to true if slider should be inside filter-values
        this.addInputElements(
            this.root.querySelector('.filter-values'),
            this.filterItem?.displayMethod,
            this.filterItem.dataType,
            addToggleSliderModeInside
        );

        if (this.filter.andOrSelectionEnabled) {
            let andOrSelect = new AndOrSelect(
                this.root.querySelector('.js-and-or-select') as HTMLDivElement,
                (v: string) => {
                    console.log('andOrSelect', v);
                    this.filter.andOrSelection = v;
                }
            );
            andOrSelect.setValue(this.filter.andOrSelection);
        }

        // If here should be subMenu, give select with options
        if (this.subMenu.items.length > 0) {
            let filterLabelSelector = this.root.querySelector('.filter-label');
            let subMenuSelector = this.root.querySelector(
                '.filter-merged-fields'
            ) as HTMLDivElement;
            if (this.subMenu.cfg) {
                let selectedparams;
                for (let i = 0; i < this.subMenu.items.length; i++) {
                    if (this.subMenu.items[i].id == this.key) {
                        selectedparams = this.subMenu.items[i].params;
                        break;
                    }
                }
                let params = Object.keys(this.subMenu.cfg);
                params.sort(
                    function (a, b) {
                        if (this.subMenu.cfg[a].order[0] != this.subMenu.cfg[b].order[0]) {
                            return this.subMenu.cfg[a].order[0] - this.subMenu.cfg[b].order[0];
                        } else {
                            return this.subMenu.cfg[a].order[1] - this.subMenu.cfg[b].order[1];
                        }
                    }.bind(this)
                );
                let html = [];
                let row = null;
                for (let i = 0; i < params.length; i++) {
                    let ctrl = this.subMenu.cfg[params[i]];
                    if (ctrl.order[0] !== row) {
                        if (html.length) {
                            html.push('</div>');
                            if (row >= 0) {
                                jQuery(subMenuSelector).append(html.join(''));
                            } else {
                                jQuery(filterLabelSelector).before(html.join(''));
                            }
                        }
                        if (ctrl.order[0] < 0) {
                            html = [
                                `<div class="filter-cluster-controls filter-controls-row" style="padding-right: 0">` +
                                    (ctrl.type == 'select'
                                        ? `<label for="filter-control-${params[i]}" style="width: 100%; text-align: right">${_(ctrl.label)}</label>`
                                        : ``) +
                                    `</div><div class="filter-cluster-controls filter-controls-row">`,
                            ];
                        } else {
                            html = ['<div class="filter-controls-row">'];
                        }
                        row = ctrl.order[0];
                    }
                    switch (ctrl.type) {
                        case 'select':
                            html.push(
                                `<div>
                                ` +
                                    (ctrl.label && (ctrl.order[0] >= 0 || ctrl.order[1] > 0)
                                        ? `<div><label for="filter-control-${params[i]}">${_(ctrl.label)}</label></div>`
                                        : ``) +
                                    `
                                <div><select id="filter-control-${params[i]}" class="filter-merged-field-selector" data-param="${params[i]}"${ctrl.values.length == 1 ? ' disabled style="display: none"' : ''}>
                                    ${ctrl.values.map(opt => `<option value="${opt}" ${selectedparams && opt == selectedparams[params[i]] ? 'selected="selected"' : ''}>${opt}</option>`).join('')}
                                </select>${ctrl.values.length == 1 ? ctrl.values[0] : ''}</div>
                            </div>`
                            );
                            break;
                        case 'checkbox':
                            let checked = selectedparams && selectedparams[params[i]];
                            html.push(`<div>
                                <div style="display: none"><input type="checkbox" id="filter-control-${params[i]}" class="filter-merged-field-selector" data-param="${params[i]}" ${checked ? ' checked' : ''} ${this.subMenuDisabled ? ' disabled' : ''}/></div>
                                <div><label for="filter-control-${params[i]}" class="checkbox-eye black-checkbox${this.subMenuDisabled ? ' disabled' : ''}"><div class="checkbox-eye-icon ${checked ? 'icon-checked' : 'icon-unchecked'}"></div><span class="checkbox-eye-label">${_(ctrl.label)}</span></label></div>
                            </div>`);
                            break;
                    }
                }
                if (html.length) {
                    html.push('</div>');
                    if (row >= 0) {
                        jQuery(subMenuSelector).append(html.join(''));
                    } else {
                        jQuery(filterLabelSelector).before(html.join(''));
                    }
                }
                let paramcontrols = this.root.querySelectorAll('.filter-merged-field-selector');
                paramcontrols.forEach(elem => {
                    elem.addEventListener('change', e => {
                        let paramvalues = {};
                        paramcontrols.forEach(elem => {
                            let param = (elem as any).getAttribute('data-param');
                            if (elem.tagName == 'SELECT') {
                                paramvalues[param] = (elem as any).value;
                            } else if (
                                (elem as any).tagName == 'INPUT' &&
                                (elem as any).getAttribute('type') == 'checkbox'
                            ) {
                                let val = (elem as any).checked;
                                paramvalues[param] = val;
                                let chkbox = this.root.querySelector(
                                    'label[for="filter-control-' + param + '"] .checkbox-eye-icon'
                                );
                                if (val) {
                                    chkbox.classList.remove('icon-unchecked');
                                    chkbox.classList.add('icon-checked');
                                } else {
                                    chkbox.classList.remove('icon-checked');
                                    chkbox.classList.add('icon-unchecked');
                                }
                            }
                        });
                        for (let i = 0; i < this.subMenu.items.length; i++) {
                            let equals = true;
                            for (let p in paramvalues) {
                                if (paramvalues[p] != this.subMenu.items[i].params[p]) {
                                    equals = false;
                                    break;
                                }
                            }
                            if (equals) {
                                console.log('And the winner is... ', this.subMenu.items[i].id);
                                /*for (let key in this.filter.getFilter()) {
                                    key = 'c_v1_percent_z2_food';
                                    if (key == this.subMenu.items[i].id) {
                                        this.setError('Ten filtr został już ustawiony.');
                                        btn_save.setAttribute('disabled', 'disabled');
                                        return;
                                    }
                                };*/
                                if (this.subMenu.items[i].id in this.filter.getFilterQueries()) {
                                    this.setError(
                                        _('A filter for these parameters is already set.')
                                    );
                                    btn_save.setAttribute('disabled', 'disabled');
                                    return;
                                }
                                btn_save.removeAttribute('disabled');
                                this.selectedSubMenuItemId = this.subMenu.items[i].id;
                                this.callbacks.onChangeSubMenu(this.selectedSubMenuItemId);
                                break;
                            }
                        }
                    });
                });
                (
                    jQuery(this.root).find(
                        'select.filter-merged-field-selector:not(:disabled)'
                    ) as any
                ).select2({
                    minimumResultsForSearch: -1,
                    disabled: this.subMenuDisabled,
                });
                if (!this.subMenuDisabled) {
                    jQuery(this.root)
                        .find('select.filter-merged-field-selector')
                        .on('select2:select', function (e) {
                            // @ts-ignore
                            var data = e.params.data;
                            data.element.parentNode.dispatchEvent(new Event('change'));
                        });
                    if (this.key in this.filter.getFilterQueries()) {
                        this.setError(_('A filter for these parameters is already set.'));
                        btn_save.setAttribute('disabled', 'disabled');
                        return;
                    }
                } else {
                    // jQuery('.select2').css('width','auto');
                    this.root
                        .querySelectorAll('.select2')
                        .forEach((el: HTMLElement) => (el.style.width = 'auto'));
                }
                if (this.slider) {
                    var paramsrows = this.root.querySelectorAll('.filter-controls-row');
                    (paramsrows[paramsrows.length - 1] as HTMLDivElement).append(
                        this.sliderModeToggle.element
                    );
                    this.sliderModeToggle.element.style.marginRight = '0';
                }
            } else {
                subMenuSelector.innerHTML = `
                    <select class="filter-merged-field-selector" data-selected-submenu="${this.selectedSubMenuItemId}" data-key="${this.key}">
                        ${this.subMenu.items.map(field => `<option value="${field.id}" ${this.key == field.id ? 'selected="selected"' : ''}>${field.name}</option>`).join('')}
                    </select>
                `;
                let selectedSubMenuItemId = subMenuSelector.querySelector(
                    '.filter-merged-field-selector'
                ) as HTMLSelectElement;

                if (this.subMenuDisabled) {
                    // Disable select if subMenuDisabled
                    selectedSubMenuItemId.setAttribute('disabled', 'disabled');
                }

                if (!this.subMenuDisabled) {
                    // Disable all options from select when key is used in filter
                    Object.keys(this.filter.getFilterQueries()).forEach(key => {
                        let option = selectedSubMenuItemId.querySelector(
                            `option[value="${key}"]`
                        ) as HTMLOptionElement;
                        if (option) option.setAttribute('disabled', 'disabled');
                    });
                }

                selectedSubMenuItemId.addEventListener('change', e => {
                    this.selectedSubMenuItemId = selectedSubMenuItemId.value;
                    this.callbacks.onChangeSubMenu(this.selectedSubMenuItemId);
                });
            }
        }

        if (btn_save) {
            btn_save.addEventListener('click', () => {
                if (this.filterItem?.displayMethod == FilterConfig.displayMethod.RELATED) {
                    if (this.values[0] == null) {
                        this.relatedFields.c = '<=';
                    } else if (this.values[1] == null) {
                        this.relatedFields.c = '>=';
                    } else {
                        this.relatedFields.c = '><';
                    }
                    this.relatedFields.v = this.values.filter(v => v != null);
                    this.callbacks.onSaveRelatedFields(this.key, this.relatedFields);
                } else {
                    let mongoCondition: QueryType = null;

                    if (this.filterItem?.displayMethod == FilterConfig.displayMethod.RADIUS) {
                        // @ts-ignore
                        mongoCondition = new Filter().getFilterItemFromGeojson(
                            SelectedArea.getGeometry()
                        );
                    } else {
                        // @ts-ignore
                        mongoCondition = new Filter().getMongoConditionFromValues(
                            this.filterItem?.displayMethod,
                            this.values,
                            this.isNegation,
                            this.filterItem?.dataType
                        );
                    }
                    console.log('updateDraggableElement, mongoCondition', mongoCondition);
                    this.callbacks.onSave(
                        this.key.split('/')[0],
                        mongoCondition,
                        this.filter.andOrSelection
                    );
                }
            });
            // btn_save.disabled = true;
        } else {
            throw new Error('btn_save is null');
        }

        if (btn_remove) {
            btn_remove.addEventListener('click', () => {
                this.callbacks.onRemove(this.key.split('/')[0]);
            });
        } else {
            throw new Error('btn_remove is null');
        }

        if (btn_show_desc) {
            btn_show_desc.addEventListener('mouseenter', () => {
                (this.root.querySelector('.filter-label') as HTMLDivElement).style.height =
                    this.root.querySelector('.filter-label').clientHeight + 'px';
                this.root.classList.add('filter-show-description');
            });
            btn_show_desc.addEventListener('mouseleave', () => {
                (this.root.querySelector('.filter-label') as HTMLDivElement).style.height = '';
                this.root.classList.remove('filter-show-description');
            });
        }
    }

    isNegationInCondition(filterItem: FilterEditorItemType): boolean {
        let { variable, condition, values } = new FilterTools().extractFromMongo(
            this.key,
            filterItem.mongoCondition
        );
        let negatives: FilterMongoCondition[] = ['$nin', '$arr_nin', '$ne', '$neq'];
        return (negatives as string[]).includes(condition) ? true : false;
    }

    parseToArray(filterItem: FilterEditorItemType): (string | number)[] {
        let { variable, condition, values } = new FilterTools().extractFromMongo(
            this.key,
            filterItem.mongoCondition
        );
        if (Array.isArray(values)) {
            return values;
        } else {
            return values ? [values] : [];
        }
    }

    addInputElements(
        root_element: HTMLDivElement,
        displayMethod: string,
        dataType: API_DataType,
        addToggleSliderMode: boolean
    ): void {
        switch (displayMethod) {
            case FilterConfig.displayMethod.BETWEEN:
                switch (dataType) {
                    case 'time':
                    case 'date': {
                        //     let values = this.parseToArray(this.filterItem);
                        //     this.createBetweenDate(
                        //         root_element,
                        //         String(values[0]),
                        //         String(values[1]),
                        //         this.filterItem.min,
                        //         this.filterItem.max,
                        //     );
                        //     break;
                        // let values = this.parseToArray(this.filterItem);
                        this.slider = this.createBetweenDateSlider(
                            root_element,
                            this.filterItem,
                            dataType,
                            addToggleSliderMode,
                            {
                                onChangePeriodMode: (periodMode: boolean) => {
                                    console.log('onChangePeriodMode', periodMode);
                                    this.sliderModeToggle.setChecked(periodMode);
                                },
                            }
                        );
                        break;
                    }
                    default: {
                        this.slider = this.createBetweenSlider(
                            root_element,
                            this.filterItem,
                            addToggleSliderMode,
                            {
                                onChangePeriodMode: (periodMode: boolean) => {
                                    console.log('onChangePeriodMode', periodMode);
                                    this.sliderModeToggle.setChecked(periodMode);
                                },
                            }
                        );
                        break;
                    }
                }
                break;
            case FilterConfig.displayMethod.SELECT_MULTIPLE: {
                // let values = (new Filter()).getValuesFromBetweenMongoCondition(this.filterItem.mongoCondition);
                let allAvailableEntries = this.filterItem.availableValues;
                let values = this.parseToArray(this.filterItem);
                if (values === null) {
                    values = [];
                }
                this.createSelectElements(
                    root_element,
                    true,
                    allAvailableEntries,
                    values,
                    this.isNegation
                );
                break;
            }
            case FilterConfig.displayMethod.SELECT_SINGLE: {
                // let values = (new Filter()).getValuesFromBetweenMongoCondition(this.filterItem.mongoCondition);
                let allAvailableEntries = this.filterItem.availableValues;
                let values = this.parseToArray(this.filterItem);
                if (values === null) {
                    values = []; // FIXME: Should be only single value (not array)
                }
                this.createSelectElements(
                    root_element,
                    false,
                    allAvailableEntries,
                    values,
                    this.isNegation
                );
                break;
            }
            case FilterConfig.displayMethod.AREA: {
                this.createMapSupport(root_element, this.filterItem.mongoCondition);
                break;
            }
            case FilterConfig.displayMethod.RADIUS: {
                this.createRadiusFilter(root_element);
                break;
            }
            case FilterConfig.displayMethod.RELATED: {
                // let cfg_filter = require('./mocks/cfgFilter__2finances_2periods.json');
                this.createRelatedElements(root_element, this.key, this.filter);
                break;
            }
            default:
            case FilterConfig.displayMethod.SINGLE: {
                let values = new Filter().getValuesFromBetweenMongoCondition(
                    this.filterItem.mongoCondition
                );
                if (values === null) {
                    values = '';
                }
                this.createSingleElements(
                    root_element,
                    new FilterTools().parseString(typeof values === 'object' ? values[0] : values),
                    this.filterItem.maxLength,
                    this.filterItem.dataType,
                    this.key
                );
                break;
            }
        }
    }

    createMapSupport(root_element: HTMLDivElement, geojson: GeojsonType) {
        console.log('createMapSupport, SelectedArea', geojson, SelectedArea.geojson);

        const cancelDrawing = () => {
            co.destroy();
            SelectedArea.cancel();
            this.callbacks.onDrawCancel(this.key);
            let header = this.szumDrawer
                .getMap()
                .getContainer()
                .querySelector('.map-header-element');
            if (header) {
                this.szumDrawer.getMap().getContainer().removeChild(header);
            }
            this.szumDrawer.getMap().modules.Controls.showAll();
            SelectedArea.setGeometry(originalGeojson);
        };

        if (geojson.properties && geojson.properties.filter === 'radius') {
            geojson = {};
        }

        let originalGeojson = JSON.parse(JSON.stringify(geojson));

        SelectedArea.setGeometry(geojson);

        // add listener when user click outside of div id=map element
        let mapElement = document.querySelector('#map') as HTMLDivElement;
        let co = clickOutside(mapElement);
        if (mapElement.__cancelDrawingHandler) {
            mapElement.removeEventListener('click_outside', mapElement.__cancelDrawingHandler);
        }
        mapElement.__cancelDrawingHandler = cancelDrawing;
        mapElement.addEventListener('click_outside', cancelDrawing);

        setTimeout(() => {
            this.callbacks.onTemporaryHideModal();
            if (!this.szumDrawer.getMap().getContainer().querySelector('.map-header-element')) {
                this.szumDrawer.getMap().modules.Controls.hideAll();
                let header = document.createElement('div');
                header.className = 'map-header-element';
                header.style.cssText =
                    'position: absolute; left: 0; top: 0; width: 100%; min-height: 40px; background-color: rgba(0, 183, 255, 0.93); color: #000; z-index: 32000; display: flex; align-items: center; padding: 5px 10px; box-sizing: border-box;';
                header.innerHTML = `
                    <div style="flex:10; display: flex; row-gap: 10px; flex-direction: column;">
                        <div>${_('filter_area_click_to_draw')}</div>
                        <div>${_('filter_area_drag_to_edit')}</div>
                    </div>
                    <div  class="filter-buttons filter-buttons-while-drawing">
                        <button class="button-filter button-save">${_('Save')}</button>
                        <button class="button-filter button-remove">${_('Cancel')}</button>
                    </div>
                `;

                let btnSave = header.querySelector('.button-save') as HTMLButtonElement;
                if (btnSave) {
                    btnSave.addEventListener('click', () => {
                        co.destroy();
                        this.callbacks.onDrawComplete(this.key, SelectedArea.save());
                        this.szumDrawer.getMap().getContainer().removeChild(header);
                        this.szumDrawer.getMap().modules.Controls.showAll();
                    });
                }

                let btnCancel = header.querySelector('.button-remove') as HTMLButtonElement;
                if (btnCancel) {
                    btnCancel.addEventListener('click', cancelDrawing);
                }
                this.szumDrawer.getMap().getContainer().appendChild(header);

                SelectedArea.edit(this.szumDrawer.getMap(), {
                    onChange: () => {
                        if (btnSave) btnSave.removeAttribute('disabled');
                    },
                });
            }
        }, 100);
    }

    createRadiusFilter(root_element: HTMLDivElement) {
        let input_element = document.createElement('div') as HTMLDivElement;
        input_element.setAttribute('class', 'filter-input-container');

        input_element.innerHTML = `
            <div style="display: flex; flex-direction: row; column-gap: 6px; align-items: center;">
                <label style="margin-bottom: 0; width: 60px; text-align: right;">${_('Center')}</label>
                <div class="filter-radius-searchform" style="width: 80%"></div>
            </div>
            <div style="display: flex; flex-direction: row; column-gap: 6px; align-items: center;">
                <label style="margin-bottom: 0; width: 60px; text-align: right;">${_('Radius')}</label>
                <div class="js-value-from-container">
<!--                    <div>-->
<!--                        <input type="text" class="js-filter-input filter-input" value="" style="width: 50px"> m-->
<!--                    </div>-->
                </div>
            </div>
            <div class="js-slider-telematics" style="margin-left: 66px;"></div>
            <div class="js-errors"></div>
        `;

        let coords: { x: number; y: number } | null = null;
        let radius: number = 100;
        let label = '';
        let oval = null;
        if (this.filterItem?.mongoCondition?.properties?.filter == 'radius') {
            coords = this.filterItem.mongoCondition.properties.coords;
            radius = this.filterItem.mongoCondition.properties.radius;
            label = this.filterItem.mongoCondition.properties.label;
            this.values = [coords, radius, label]; // as [{ x: number, y: number }, number, string];
        } else {
            this.values = [null, 100, ''];
        }

        let visualize = function () {
            SelectedArea.resetGeometry();
            if (coords && radius) {
                // @ts-ignore
                let geom = proj4(
                    'EPSG:4326',
                    'EPSG:2180',
                    JSON.parse(JSON.stringify(this.values[0]))
                );
                // @ts-ignore
                geom = new GeometryFactory()
                    .createPoint(new Coordinate(geom.x, geom.y))
                    .buffer(this.values[1]);
                // @ts-ignore
                geom = [
                    geom
                        .getExteriorRing()
                        .getCoordinates()
                        .map(function (c) {
                            c = proj4('EPSG:2180', 'EPSG:4326', c);
                            return [c.x, c.y];
                        }),
                ];
                SelectedArea.setGeometry({
                    type: 'Polygon',
                    coordinates: geom,
                    properties: {
                        filter: 'radius',
                        coords: this.values[0],
                        radius: this.values[1],
                        label: this.values[2],
                    },
                });
                // SelectedArea.drawArea();
                new AreasCreator(this.szumDrawer.getMap()).zoomToMapElement({
                    geometry: { type: 'Point', coordinates: [coords.x, coords.y] },
                    buffer: radius,
                });
                this.root.querySelector('.js-filter-save').disabled = false;
            } else {
                this.root.querySelector('.js-filter-save').disabled = true;
            }
        }.bind(this);

        let onCoordsSet = function (newcoords: { x: number; y: number } | null) {
            console.log('onCoordsSet', newcoords);
            let inp = input_element.querySelectorAll('input')[0] as HTMLInputElement;
            if (newcoords) {
                inp.classList.remove('filter-input-error');
                inp.classList.add('filter-input-valid');
                this.setError();
            } else if (inp.classList.contains('search-point-form-error')) {
                inp.classList.add('filter-input-error');
                inp.classList.remove('filter-input-valid');
                this.setError(inp.getAttribute('title'));
            }

            if (newcoords !== null) {
                coords = this.values[0] = newcoords;
            }
            label = this.values[2] = inp.value;
            visualize();
        }.bind(this);

        this.searchform = new SearchPointForm(
            input_element.querySelector('.filter-radius-searchform'),
            {
                coords: coords,
                label: label,
                validResult: function (item) {
                    return (
                        item.type == 'coords' ||
                        (item.type == 'place' &&
                            ['PLACE', 'BUILDING'].indexOf(item.objecttype) >= 0)
                    );
                },
            },
            {
                onCoordinateSet: function (coords: { x: number; y: number }) {
                    console.log('onCoordinateSet', coords);
                    onCoordsSet(coords);
                    input_element.querySelectorAll('input')[1].focus();
                },
                onCoordinateUnset: function () {
                    onCoordsSet(null);
                    console.log('onCoordinateUnset');
                },
                onClickLocationRequest: function () {
                    this.callbacks.onTemporaryHideModal();
                    this.szumDrawer.getMap().modules.Controls.hideAll();
                    let mapClickListener = this.szumDrawer.getMap().listenOnce(
                        'onClick',
                        function (evt) {
                            this.szumDrawer.getMap().getContainer().removeChild(header);
                            this.szumDrawer.getMap().modules.Controls.showAll();
                            this.searchform.setCoordinate(evt.coords, true);
                            onCoordsSet(evt.coords);
                            this.callbacks.onShowModal();
                            mapClickListener = null;
                        }.bind(this)
                    );
                    let header = document.createElement('div');
                    header.className = 'map-header-element';
                    header.style.cssText =
                        'position: absolute; left: 0; top: 0; width: 100%; height: auto; color: #000; z-index: 32000; display: flex; align-items: center; padding: 5px 10px; box-sizing: border-box; background-color: #ffffffee; color: #000 !important; font-size: 12px;';
                    header.innerHTML = `
                    <div style="flex:10">
                        <div>${_('filter_radius_click_center')}</div>
                    </div>
                    <div class="filter-buttons  filter-buttons-while-drawing">
                        <button class="button-filter js-filter-save button-remove">${_('Cancel')}</button>
                    </div>
                `;
                    header.querySelector('.button-remove').addEventListener(
                        'click',
                        function () {
                            this.szumDrawer.getMap().unlistenByKey(mapClickListener);
                            this.szumDrawer.getMap().getContainer().removeChild(header);
                            this.szumDrawer.getMap().modules.Controls.showAll();
                            this.searchform.unsetCoordinate();
                            this.callbacks.onShowModal();
                            input_element.querySelectorAll('input')[1].focus();
                        }.bind(this)
                    );
                    this.szumDrawer.getMap().getContainer().appendChild(header);
                }.bind(this),
            }
        );
        input_element.querySelector('.targeo-ftsform-input').classList.add('filter-input');

        // let radiusinput = input_element.querySelectorAll('input')[1];
        // radiusinput.value = radius.toString() ?? '';
        // radiusinput.addEventListener('change', ((e) => {
        //     let inp = e.target;
        //     let val = inp.value.replace(/\s/g, '');
        //     if (val.match(/^\d+/) && parseInt(val) > 0) {
        //         radius = this.values[1] = parseInt(val);
        //         inp.classList.remove('filter-input-error');
        //         inp.classList.add('filter-input-valid');
        //         this.setError();
        //         visualize();
        //     } else {
        //         inp.classList.add('filter-input-error');
        //         inp.classList.remove('filter-input-valid');
        //         this.setError('Niepoprawna wartość');
        //     }
        // }).bind(this));

        let formInput = input_element.querySelector('.targeo-ftsform-input') as HTMLInputElement;
        if (formInput) formInput.value = label;
        visualize();
        root_element.appendChild(input_element);

        new sliderTelematicsSingle(
            root_element.querySelector('.js-slider-telematics'),
            {
                min: 10,
                max: 50000,
                currentValue: radius,
                unit: 'm',
                roundValues: false,
                keepInitialValueInSliderRange: true,
            },
            {
                onSliderChange: (currentValue: number) => {
                    radius = this.values[1] = currentValue;
                    this.setError();
                    visualize();
                },
            }
        );
    }

    createRelatedElements(root_element: HTMLDivElement, key: string, filter: Filter) {
        this.relatedFields =
            filter.getFilterRelatedItem(key) ??
            filter.getFilterRelatedItemDefault(key, this.filterItem, 'value');

        let humanFriendlyMonths = [
            _('January'),
            _('February'),
            _('March'),
            _('April'),
            _('May'),
            _('June'),
            _('July'),
            _('August'),
            _('September'),
            _('October'),
            _('November'),
            _('December'),
        ];
        let periods = [];
        let period = this.filterItem.period_range.min;
        do {
            periods.push(period);
            let periodarr = period.split('-').map(e => parseInt(e));
            if (periodarr[1] < 12) {
                periodarr[1]++;
            } else {
                periodarr[0]++;
                periodarr[1] = 1;
            }
            period = periodarr.join('-').replace(/-(\d)$/, '-0$1');
        } while (period <= this.filterItem.period_range.max);

        const getSliderRange = (type: string) => {
            let rootForSpinner: HTMLDivElement = root_element.closest(
                '.filter_list_columns'
            ) as HTMLDivElement;
            new Spinner(rootForSpinner).show();
            const formData = new FormData();
            const data = {
                f: this.relatedFields.id,
                r: this.relatedFields.db_name,
                p: [
                    relatedPeriodBase.value,
                    type == 'period' ? relatedPeriodCompare.value : '',
                ].join('/'),
                fp: this.relatedFields.comparing_field,
            };
            for (const name in data) {
                formData.append(name, data[name]);
            }
            fetch('?action=rpc_relatedRange', {
                method: 'POST',
                body: formData,
            })
                .then(res => res.json())
                .then(res => {
                    if (typeof res.range.min == 'number' && typeof res.range.max == 'number') {
                        this.filterItem.min = res.range.min;
                        this.filterItem.max = res.range.max;
                        this.filterItem.unit =
                            this.relatedFields.type == 'period' ? '%' : this.relatedFields.unit;
                        let sliderContainer = this.root.querySelector(
                            '.js-slider-container'
                        ) as HTMLDivElement;
                        if (sliderContainer) {
                            sliderContainer.innerHTML = '';
                            this.slider = this.createBetweenSlider(
                                sliderContainer,
                                this.filterItem,
                                this.root.querySelector('.js-slider-mode-toggle') as HTMLDivElement,
                                {
                                    onChangePeriodMode: (periodMode: boolean) => {
                                        console.log('onChangePeriodMode', periodMode);
                                        this.sliderModeToggle.setChecked(periodMode);
                                    },
                                }
                            );
                        }
                        new Spinner(rootForSpinner).hide();
                    }
                })
                .catch(err => {
                    console.error(err.message);
                    new Spinner(rootForSpinner).hide();
                    alert(_('An error occurred while fetching the value range'));
                });
        };

        const showHideSection = (value: string) => {
            let elPeriod = el.querySelector('.js-type-period') as HTMLInputElement;
            if (elPeriod) {
                elPeriod.style.display = value == 'period' ? 'flex' : 'none';
            }
        };

        let el = document.createElement('div');
        el.classList.add('filter-values');
        el.style.display = 'flex';
        el.style.flexDirection = 'column';
        el.style.justifyContent = 'start';
        el.style.rowGap = '5px';

        el.innerHTML = `
        <div style="display:flex;column-gap: 5px;">
            <div style="display: flex; flex-direction: column; justify-content: end;">
                <span style="font-size: 11px; opacity: 0.6; margin: 2px 5px;">w okresie:</span>
                <select class="related-period-based  input-select" style="width:140px; "></select>
            </div>
            <div style="display: flex; flex-direction: column; justify-content: end;" class="js-compare">
            </div>
            <div style="display: flex; flex-direction: column; justify-content: end;" class="js-type-period">
                <span style="font-size: 11px; opacity: 0.6; margin: 2px 5px;">do okresu:</span>
                <select class="related-period-related  input-select" style="width:140px;"></select>
            </div>
            <div class="js-slider-mode-toggle" style="display: flex; align-items: end;"></div>
        </div>
        <div style="display: flex; flex-direction: row;">
            <div class="js-slider-container" style="padding-right: 50px"></div>
            <div class="related-unit" style="padding-top: 35px; display: none; "></div>
        </div>

        <div class="js-errors"></div>
        `;

        const validateAll = () => {
            let errors = new ErrorSupport(el.querySelector('.js-errors') as HTMLDivElement);

            if (errors.getErrors().length) {
                this.setError(errors.getErrors().join(', '));
                return false;
            }

            this.setError('');
            return true;
        };

        let relatedPeriodBase = el.querySelector('.related-period-based') as HTMLSelectElement;
        relatedPeriodBase.innerHTML = periods
            .map(
                p =>
                    `<option value="${p}">${humanFriendlyMonths[parseInt(p.split('-')[1]) - 1] + ' ' + p.split('-')[0]}</option>`
            )
            .join('');
        relatedPeriodBase.value = this.relatedFields.base_period as string;
        relatedPeriodBase.addEventListener('change', e => {
            this.relatedFields.base_period = relatedPeriodBase.value;
            if (relatedPeriodCompare.value == relatedPeriodBase.value) {
                let val = relatedPeriodBase.value.split('-').map(e => parseInt(e));
                if (relatedPeriodBase.value == this.filterItem.period_range.min) {
                    if (val[1] < 12) {
                        val[1]++;
                    } else {
                        val[0]++;
                        val[1] = 1;
                    }
                } else {
                    if (val[1] > 1) {
                        val[1]--;
                    } else {
                        val[0]--;
                        val[1] = 12;
                    }
                }
                relatedPeriodCompare.value = val[0] + '-' + ('0' + val[1]).substr(-2);
                relatedPeriodCompare.dispatchEvent(new Event('change'));
            } else {
                getSliderRange(this.relatedFields.type);
            }
            relatedPeriodCompare.querySelector('option[disabled="1"]').removeAttribute('disabled');
            relatedPeriodCompare
                .querySelector('option[value="' + relatedPeriodBase.value + '"]')
                .setAttribute('disabled', '1');
            this.callbacks.onChangeAnything(key);
            validateAll();
        });

        let relatedPeriodCompare = el.querySelector('.related-period-related') as HTMLSelectElement;
        relatedPeriodCompare.innerHTML = periods
            .map(
                p =>
                    `<option value="${p}">${humanFriendlyMonths[parseInt(p.split('-')[1]) - 1] + ' ' + p.split('-')[0]}</option>`
            )
            .join('');
        relatedPeriodCompare.value = this.relatedFields.second_period as string;
        relatedPeriodCompare.addEventListener('change', e => {
            this.callbacks.onChangeAnything(key);
            this.relatedFields.second_period = relatedPeriodCompare.value;
            getSliderRange(this.relatedFields.type);
            validateAll();
        });
        relatedPeriodCompare
            .querySelector('option[value="' + relatedPeriodBase.value + '"]')
            .setAttribute('disabled', '1');

        el.querySelector('.js-compare').appendChild(
            Checkbox(
                {
                    checked: this.relatedFields.type == 'period',
                    label: _('compare'),
                    disabled: false,
                    mode: 'black-checkbox',
                },
                {
                    onChange: checked => {
                        let type = checked ? 'period' : 'value';
                        let unit = type == 'period' ? '%' : this.relatedFields.unit;
                        relatedUnit.innerHTML = unit;
                        this.slider.setUnit(unit);
                        //relatedUnit.innerHTML = this.relatedFields.unit = type == 'period' ? '%' : this.filterItem.unit;
                        //this.slider.setUnit(this.relatedFields.unit);
                        this.callbacks.onChangeAnything(key);
                        showHideSection(type);
                        this.relatedFields.type = type;
                        getSliderRange(this.relatedFields.type);
                        validateAll();
                    },
                }
            ).element
        );

        showHideSection(this.relatedFields.type);

        let relatedUnit = el.querySelector('.related-unit') as HTMLSpanElement;
        relatedUnit.innerHTML = this.relatedFields.type == 'period' ? '%' : this.relatedFields.unit;

        root_element.appendChild(el);

        getSliderRange(this.relatedFields.type);

        validateAll();
    }

    createBetweenDate(
        root_element: HTMLDivElement,
        startDateStr: string,
        endDateStr: string,
        minDateTimestamp: number | null,
        maxDateTimestamp: number | null
    ) {
        // console.log('createBetweenElements', value1, value2);

        let input_element = document.createElement('div') as HTMLDivElement;

        input_element.setAttribute('class', 'filter-input-container');
        input_element.innerHTML = `
            <div style="display:flex; flex-direction:row; column-gap:6px; margin-bottom: 10px;">
                <div style="align-items: center; display: flex;">w dniach</div>
                <div class="js-value-from-container">
                    <div class="js-calendar"></div>
                </div>
            </div>
            <div class="js-errors"></div>
            `;

        let errorSupport = new ErrorSupport(
            input_element.querySelector('.js-errors') as HTMLDivElement
        );

        // https://www.daterangepicker.com/
        // https://momentjs.com/

        let dateFormat = 'YYYY-MM-DD';

        // @ts-ignore moment is undefined
        let m = moment;

        const updateDateElement = (start: any, end: any) => {
            console.log(
                'CalendarRange, updateDateElement',
                m(start).format(dateFormat),
                m(end).format(dateFormat),
                ', min-max: ',
                minDateTimestamp,
                maxDateTimestamp
            );
            if (m(start).isValid() && m(end).isValid()) {
                this.values = [m(start).format(dateFormat), m(end).format(dateFormat)];
                errorSupport.clearAll();
            } else {
                // if (!m(start).isValid()) {
                //     errorSupport.addError('Niepoprawny format wartości <span style="font-weight:600;">od</span>');
                // }
                // if (!m(end).isValid()) {
                //     errorSupport.addError('Niepoprawny format wartości <span style="font-weight:600;">do</span>');
                // }
            }
            this.setError([...errorSupport.getErrors(), ...errorSupport.getInfos()].join(', '));
        };

        root_element.appendChild(input_element);

        let calendar_element = new CalendarRange(
            input_element.querySelector('.js-calendar') as HTMLDivElement,
            {
                dateFormat,
                startDate: startDateStr,
                endDate: endDateStr,
                minDateTimestamp,
                maxDateTimestamp,
            },
            {
                onChange: (start: any, end: any) => {
                    console.log('CalendarRange, onChange', start, end);
                    updateDateElement(start, end);
                },
                onValidation: (valid: boolean) => {
                    console.log('CalendarRange, onValidation', valid);
                    if (valid) {
                        errorSupport.clearAll();
                    } else {
                        errorSupport.addError('Wybierz zakres dat');
                    }
                    this.setError(
                        [...errorSupport.getErrors(), ...errorSupport.getInfos()].join(', ')
                    );
                },
            }
        );

        updateDateElement(startDateStr, endDateStr);

        // this.setError([...errorSupport.getErrors(), ...errorSupport.getInfos()].join(', '));
    }

    createBetweenDateSlider(
        root_element: HTMLDivElement,
        filterItem: FilterEditorItemType,
        dataType: API_DataType,
        addToggleSliderMode: boolean | HTMLDivElement,
        callbacks: {
            onChangePeriodMode: (periodMode: boolean) => void;
        }
    ): sliderTelematicsRange {
        // console.log('allowToExtendRange, filterItem', filterItem);

        let isDirty: boolean = true;
        let resolutionConverter = new ResolutionConverter(dataType);
        let input_element = document.createElement('div') as HTMLDivElement;
        let colorizeSlider: sliderTelematicsRange | null = null;
        let relativeMode: boolean = false;

        input_element.setAttribute('class', 'filter-input-container');
        input_element.setAttribute(
            'style',
            // 'background-color: #6c6b6a;\n' +
            'display: flex;\n' + 'justify-content: center;'
        );
        input_element.innerHTML = `
            <div style="display: flex; flex-direction: row; align-items: center; column-gap: 10px; margin-bottom: 17px; ">
                <div class="toggle-slider-mode"></div>
                <div class="toggle-slider-mode-relative"></div>
            </div>
            <div class="js-slider"></div>
            <div class="js-errors"></div>
            `;

        let sliderContainer = input_element.querySelector('.js-slider') as HTMLDivElement;

        const validateAll = (
            values: number[],
            sliderData: SliderData | null,
            isDirty: boolean
        ): boolean => {
            if (sliderData === null) return false;
            let isValid: boolean = true;

            if (sliderData.getVisibleMax() - sliderData.getVisibleMin() <= 0) {
                this.setError(
                    _(
                        'Min and max data values are invalid for attribute {attribute}. Contact your administrator to update this data.',
                        { values: { attribute: this.key } }
                    )
                );
                return false;
            }

            if (sliderData.getInfinityFrom() && sliderData.getInfinityTo()) {
                isValid = false;
                if (isDirty) {
                    this.setError(
                        _('The range cannot be open on both sides. Change one of the values.')
                    );
                    console.warn(
                        'createBetweenSlider, validateAll, Zakres nie może być otwarty z obu stron.',
                        sliderData.getInfinityFrom(),
                        sliderData.getInfinityTo(),
                        sliderData.data.blocks
                    );
                } else {
                    this.setError(_('Set the filter range'));
                }
            }

            if (isValid) {
                this.values = values;
                this.setError('');
                return true;
            } else {
                return false;
            }
        };

        let updatedValues = explodeValuesFromFilterItem(
            filterItem,
            resolutionConverter,
            dataType,
            relativeMode,
            isDirty
        );
        let min = updatedValues.min;
        let max = updatedValues.max;
        let currentValue1 = updatedValues.currentValue1;
        let currentValue2 = updatedValues.currentValue2;
        relativeMode = updatedValues.relativeMode;
        isDirty = updatedValues.isDirty;

        console.log('createBetweenSlider, filterItem', filterItem);

        this.sliderModeToggle = Checkbox(
            {
                label: '+/-',
                checked: false,
                disabled: false,
                mode: 'black-checkbox',
            },
            {
                onChange: (checked: boolean) => {
                    if (colorizeSlider) {
                        console.log('time-period, sliderModeToggle, onChange', checked);
                        colorizeSlider.setPeriodMode(checked);
                        validateAll(this.values, colorizeSlider.getSliderData(), isDirty);
                    }
                },
            }
        );

        if (addToggleSliderMode) {
            let el = (
                addToggleSliderMode === true
                    ? input_element.querySelector('.toggle-slider-mode')
                    : addToggleSliderMode
            ) as HTMLDivElement;
            if (el) {
                el.innerHTML = '';
                el.appendChild(this.sliderModeToggle.element);
            }
        }

        let sliderModeRelativeToggle = Checkbox(
            {
                label: 'w relacji do dzisiaj',
                checked: relativeMode,
                disabled: false,
                mode: 'black-checkbox',
            },
            {
                onChange: (checked: boolean) => {
                    console.log('time-period, sliderModeRelativeToggle, onChange', checked);
                    if (colorizeSlider) {
                        if (checked) {
                            relativeMode = true;
                            resolutionConverter.setResolution('today');
                            colorizeSlider.updateSettings(
                                RelativeSliderSettingsOrNot.getSettings(
                                    relativeMode,
                                    'today:-14',
                                    'today:14',
                                    'today:-3',
                                    'today:3',
                                    'dzisiaj'
                                )
                            );
                        } else {
                            relativeMode = false;
                            resolutionConverter.setResolution(dataType);
                            colorizeSlider.updateSettings(
                                RelativeSliderSettingsOrNot.getSettings(
                                    relativeMode,
                                    ResolutionDate.getDateFromDays(min),
                                    ResolutionDate.getDateFromDays(max),
                                    '2024-01-15',
                                    '2024-02-15',
                                    ''
                                )
                            );
                        }
                        // isDirty = true;
                        colorizeSlider.setPeriodMode(this.sliderModeToggle.isChecked());
                        validateAll(this.values, colorizeSlider.getSliderData(), isDirty);
                    } else {
                        console.warn(
                            'createBetweenSlider, sliderModeRelativeToggle, colorizeSlider is not defined'
                        );
                    }
                },
            }
        );

        if (dataType === 'date') {
            let el = input_element.querySelector('.toggle-slider-mode-relative') as HTMLDivElement;
            if (el) {
                el.innerHTML = '';
                el.appendChild(sliderModeRelativeToggle.element);
            }
        }

        // console.log('allowToExtendRange, currentValue1, currentValue2', currentValue1, currentValue2);

        let settings = {
            min: min,
            max: max,
            currentValue1: currentValue1,
            currentValue2: currentValue2,
            unit: filterItem.unit,
            roundValues: true,
            dangerZoneBelowValue: 0,
            resolution: dataType,
            allowToExtendRange: true,
        };

        if (relativeMode) {
            settings = {
                ...settings,
                ...RelativeSliderSettingsOrNot.getSettings(
                    relativeMode,
                    'today:-14',
                    'today:14',
                    ResolutionToday.getTodayStringFromDays(currentValue1),
                    ResolutionToday.getTodayStringFromDays(currentValue2),
                    'dzisiaj'
                ),
            };
        }

        colorizeSlider = new sliderTelematicsRange(sliderContainer, settings, {
            onSliderChange: (currentValue1, currentValue2) => {
                console.debug(
                    'time-period, sliderTelematicsRange, onSliderChange',
                    currentValue1,
                    currentValue2
                );
                this.values = [currentValue1, currentValue2];
                this.callbacks.onChangeAnything(this.key);
                this.values = getValuesIncludingInfinityFromSliderOutput(
                    currentValue1,
                    currentValue2,
                    resolutionConverter
                );
                console.debug('sliderTelematicsRange, onSliderChange, this.values', this.values);
                isDirty = true;
                validateAll(this.values, colorizeSlider.getSliderData(), isDirty);
            },
            onChangePeriodMode: (periodMode: boolean, currentValue1, currentValue2) => {
                this.sliderModeToggle.setChecked(periodMode);
                console.debug(
                    'time-period, sliderTelematicsRange, onChangePeriodMode',
                    periodMode,
                    currentValue1,
                    currentValue2
                );
                callbacks.onChangePeriodMode(periodMode);
                this.values = getValuesIncludingInfinityFromSliderOutput(
                    currentValue1,
                    currentValue2,
                    resolutionConverter
                );
                validateAll(this.values, colorizeSlider.getSliderData(), isDirty);
            },
        });

        this.values = getValuesIncludingInfinityFromSliderOutput(
            currentValue1,
            currentValue2,
            resolutionConverter
        );

        root_element.appendChild(input_element);

        let marginLeft = 55;
        let jsSliderElementTgUiColorMultiRangeValueMin = input_element.querySelector(
            '.js-slider .tg-ui-color-multi-range-value-min span'
        ) as HTMLSpanElement;
        if (jsSliderElementTgUiColorMultiRangeValueMin) {
            marginLeft = 35 + Math.max(20, jsSliderElementTgUiColorMultiRangeValueMin.clientWidth);
        }
        new featureKeepAlignmentWithSlider().setMargin(
            input_element.closest('.filter-values-container') as HTMLDivElement,
            marginLeft
        );

        console.log(
            'time-period, createBetweenSlider, dataType ',
            dataType,
            ' this.values',
            this.values,
            ', colorizeSlider.getSliderData()',
            colorizeSlider.getSliderData()
        );
        validateAll(this.values, colorizeSlider.getSliderData(), isDirty);

        return colorizeSlider;
    }

    createBetweenSlider(
        root_element: HTMLDivElement,
        filterItem: FilterEditorItemType,
        addToggleSliderMode: boolean | HTMLDivElement,
        callbacks: {
            onChangePeriodMode: (periodMode: boolean) => void;
        }
    ): sliderTelematicsRange {
        let isDirty: boolean = true;

        let input_element = document.createElement('div') as HTMLDivElement;

        input_element.setAttribute('class', 'filter-input-container');
        input_element.setAttribute(
            'style',
            // 'background-color: #6c6b6a;\n' +
            'display: flex;\n' + 'justify-content: center;'
        );
        input_element.innerHTML = `
            <div class="toggle-slider-mode"></div>
            <div class="js-slider"></div>
            <div class="js-errors"></div>
            `;

        const validateAll = (
            values: number[],
            sliderData: SliderData,
            isDirty: boolean
        ): boolean => {
            let isValid: boolean = true;

            if (sliderData.getVisibleMax() - sliderData.getVisibleMin() <= 0) {
                this.setError(
                    _(
                        'Min and max data values are invalid for attribute {attribute}. Contact your administrator to update this data.',
                        { values: { attribute: this.key } }
                    )
                );
                return false;
            }

            if (sliderData.getInfinityFrom() && sliderData.getInfinityTo()) {
                isValid = false;
                if (isDirty) {
                    this.setError(
                        _('The range cannot be open on both sides. Change one of the values.')
                    );
                    console.warn(
                        'createBetweenSlider, validateAll, Zakres nie może być otwarty z obu stron.',
                        sliderData.getInfinityFrom(),
                        sliderData.getInfinityTo(),
                        sliderData.data.blocks
                    );
                } else {
                    this.setError(_('Set the filter range'));
                }
            }

            if (isValid) {
                this.values = values;
                this.setError('');
                return true;
            } else {
                return false;
            }
        };

        const getValuesIncludingInfinityFromSliderOutput = (
            currentValue1: number | false,
            currentValue2: number | false
        ) => {
            let values: (number | null)[] = [];
            if (currentValue1 === false && currentValue2 !== false) {
                values = [null, currentValue2];
            } else if (currentValue2 === false && currentValue1 !== false) {
                values = [currentValue1, null];
            } else if (currentValue1 !== false && currentValue2 !== false) {
                values = [currentValue1, currentValue2];
            }
            return values;
        };

        filterItem.min = Math.round(parseFloat(filterItem.min as unknown as string) * 100) / 100;
        filterItem.max = Math.round(parseFloat(filterItem.max as unknown as string) * 100) / 100;

        let currentValue1: number | false = false;
        let currentValue2: number | false = false;

        // TODO: Refactor this / move to getValuesFromRelatedFields()
        try {
            if (this.relatedFields) {
                if (this.relatedFields.c) {
                    switch (this.relatedFields.c) {
                        case '>=':
                            currentValue1 = Math.max(
                                this.relatedFields.v[0] as number,
                                filterItem.min
                            );
                            currentValue2 = false;
                            break;
                        case '<=':
                            currentValue1 = false;
                            currentValue2 = Math.min(
                                this.relatedFields.v[0] as number,
                                filterItem.max
                            );
                            break;
                        default:
                            try {
                                if (this.relatedFields.v.length === 2) {
                                    currentValue1 = Math.max(
                                        this.relatedFields.v[0] as number,
                                        filterItem.min
                                    );
                                    currentValue2 = Math.min(
                                        this.relatedFields.v[1] as number,
                                        filterItem.max
                                    );
                                }
                            } catch (e) {
                                console.warn('createBetweenSlider, switch, default, catch', e);
                            }
                    }
                } else {
                    isDirty = false;
                    currentValue1 = false;
                    currentValue2 = false;
                }
            } else {
                let mongoKey = Object.keys(filterItem.mongoCondition)[0];
                if (
                    typeof mongoKey !== 'undefined' &&
                    typeof filterItem.mongoCondition[mongoKey] !== 'undefined'
                ) {
                    switch (mongoKey) {
                        case '$gte':
                        case '$gt':
                            currentValue1 = Math.max(
                                filterItem.mongoCondition[mongoKey],
                                filterItem.min
                            );
                            currentValue2 = false;
                            break;
                        case '$lte':
                        case '$lt':
                            currentValue1 = false;
                            currentValue2 = Math.min(
                                filterItem.mongoCondition[mongoKey],
                                filterItem.max
                            );
                            break;
                        default:
                            currentValue1 = Math.max(
                                filterItem.mongoCondition[mongoKey][0],
                                filterItem.min
                            );
                            currentValue2 = Math.min(
                                filterItem.mongoCondition[mongoKey][1],
                                filterItem.max
                            );
                    }
                } else {
                    isDirty = false;
                    currentValue1 = false;
                    currentValue2 = false;
                }
            }
        } catch (e) {
            console.error('createBetweenSlider, filterItem', filterItem);
            console.error('createBetweenSlider, error', e);
        }

        this.sliderModeToggle = Checkbox(
            {
                label: '+/-',
                checked: false,
                disabled: false,
                mode: 'black-checkbox',
            },
            {
                onChange: (checked: boolean) => {
                    colorizeSlider.setPeriodMode(checked);
                    validateAll(this.values, colorizeSlider.getSliderData(), isDirty);
                },
            }
        );

        if (addToggleSliderMode) {
            let el = (
                addToggleSliderMode === true
                    ? input_element.querySelector('.toggle-slider-mode')
                    : addToggleSliderMode
            ) as HTMLDivElement;
            if (el) {
                el.innerHTML = '';
                el.appendChild(this.sliderModeToggle.element);
            }
        }

        let colorizeSlider = new sliderTelematicsRange(
            input_element.querySelector('.js-slider'),
            {
                min: filterItem.min,
                max: filterItem.max,
                currentValue1: currentValue1,
                currentValue2: currentValue2,
                unit: filterItem.unit,
                roundValues: filterItem.dataType === 'int',
                dangerZoneBelowValue: 0,
                resolution: filterItem.dataType === 'date' ? 'date' : 'number',
            },
            {
                onSliderChange: (currentValue1, currentValue2) => {
                    console.debug(
                        'sliderTelematicsRange, onSliderChange',
                        currentValue1,
                        currentValue2
                    );
                    this.values = [currentValue1, currentValue2];
                    this.callbacks.onChangeAnything(this.key);
                    this.values = getValuesIncludingInfinityFromSliderOutput(
                        currentValue1,
                        currentValue2
                    );
                    isDirty = true;
                    validateAll(this.values, colorizeSlider.getSliderData(), isDirty);
                },
                onChangePeriodMode: (periodMode: boolean, currentValue1, currentValue2) => {
                    // alert('onChangePeriodMode: ' + periodMode);
                    //toggleSliderMode.setChecked(periodMode);
                    this.sliderModeToggle.setChecked(periodMode);
                    console.debug(
                        'sliderTelematicsRange, onChangePeriodMode',
                        periodMode,
                        currentValue1,
                        currentValue2
                    );
                    callbacks.onChangePeriodMode(periodMode);
                    this.values = getValuesIncludingInfinityFromSliderOutput(
                        currentValue1,
                        currentValue2
                    );
                    validateAll(this.values, colorizeSlider.getSliderData(), isDirty);
                },
            }
        );

        this.values = getValuesIncludingInfinityFromSliderOutput(currentValue1, currentValue2);

        root_element.appendChild(input_element);

        // filter-values-container
        // let parentSliderElement = root_element.parentNode as HTMLDivElement;
        // let jsSliderElement = input_element.querySelector('.js-slider') as HTMLDivElement;
        // let jsSliderElementTgUiColorMultiRangeValueMin = input_element.querySelector('.js-slider .tg-ui-color-multi-range-value-min span') as HTMLSpanElement;
        // if (parentSliderElement && jsSliderElement && jsSliderElementTgUiColorMultiRangeValueMin) {
        //     parentSliderElement.style.paddingLeft = (50 + jsSliderElementTgUiColorMultiRangeValueMin.clientWidth) + 'px';
        //     jsSliderElement.style.marginLeft = String(0);
        // }

        let marginLeft = 55;
        let jsSliderElementTgUiColorMultiRangeValueMin = input_element.querySelector(
            '.js-slider .tg-ui-color-multi-range-value-min span'
        ) as HTMLSpanElement;
        if (jsSliderElementTgUiColorMultiRangeValueMin) {
            marginLeft = 35 + Math.max(20, jsSliderElementTgUiColorMultiRangeValueMin.clientWidth);
        }
        new featureKeepAlignmentWithSlider().setMargin(
            input_element.closest('.filter-values-container') as HTMLDivElement,
            marginLeft
        );

        validateAll(this.values, colorizeSlider.getSliderData(), isDirty);

        return colorizeSlider;
    }

    createSelectElements(
        root_element: HTMLDivElement,
        multiple: boolean,
        allAvailableEntries: { id: number | string; name: string }[],
        selectedIds: (number | string)[],
        isNegation: boolean
    ) {
        // console.log('createSelectElements', multiple, allAvailableEntries, selectedIds, isNegation)
        let input_element = document.createElement('div');
        input_element.setAttribute('class', 'filter-input-container');
        input_element.setAttribute('style', 'display: flex; flex-direction: row;');
        input_element.innerHTML = `
        <div style="display: flex; flex-direction: column; row-gap: 10px;">
            <div style="display: flex; flex-direction: column; column-gap: 6px; row-gap: 10px; margin-right: 10px;">
                <div class="js-is-negation"></div>
                <div class="sumo-select-container"></div>
            </div>
            <div class="js-errors"></div>
        </div>
            `;

        this.values = selectedIds;

        input_element.querySelector('.js-is-negation').appendChild(
            Checkbox(
                {
                    checked: this.isNegation,
                    label: _('negation'),
                    disabled: false,
                    mode: 'black-checkbox',
                },
                {
                    onChange: checked => {
                        this.callbacks.onChangeAnything(this.key);
                        this.isNegation = checked;
                        this.setError();
                        if (
                            typeof this.values !== 'string' &&
                            typeof this.values !== 'number' &&
                            this.values.hasOwnProperty('length') &&
                            this.values.length === 0
                        ) {
                            this.setError(_('Select at least one value'));
                        }
                    },
                }
            ).element
        );

        // console.log('createSelectElements, FilterSelect, allAvailableEntries', allAvailableEntries);

        const onChangeHandler = (selectedIds: (string | number)[]) => {
            this.callbacks.onChangeAnything(this.key);
            this.setError();
            if (selectedIds.length === 0) {
                this.setError(_('Select at least one value'));
                return false;
            }
            // console.log('multiselect, close, HumanEditorItem', selectedIds);
            this.values = selectedIds;
            return true;
        };

        let el = input_element.querySelector('.sumo-select-container') as HTMLDivElement;

        if (el) {
            let multi = new MultiSelectWide(
                {
                    parentRoot: el,
                    multiple: true,
                    availableEntries: allAvailableEntries,
                    selectedIds: selectedIds,
                    forceConvertIdToString: true,
                },
                {
                    onChange: function (selectedIds: (string | number)[]) {
                        // console.log('onChange', selectedIds);
                        onChangeHandler(selectedIds);
                    },
                }
            );
        }

        // input_element.querySelector('.sumo-select-container').appendChild(
        //     FilterSelect({
        //         multiple: multiple,
        //         allAvailableEntries: allAvailableEntries,
        //         selectedIds: selectedIds,
        //         parentRoot: this.root
        //     }, {
        //         onChangeAndClose: (selectedIds) => onChangeHandler(selectedIds),
        //         onChangeWhileOpen: (selectedIds) => onChangeHandler(selectedIds),
        //     })
        // );
        //

        root_element.appendChild(input_element);

        // Check if there is no selected values at init
        if (selectedIds.length === 0) {
            this.setError(_('Select at least one value'));
        } else {
            // console.log('createSelectElements, setError');
            this.setError();
        }
    }

    createSingleElements(
        root_element: HTMLDivElement,
        value: string,
        maxLength: number,
        dataType: API_DataType,
        key: string
    ) {
        let input_element = document.createElement('div') as HTMLDivElement;
        input_element.setAttribute('class', 'filter-input-container');
        input_element.innerHTML = `
                <div class="js-value-to-container"></div>
                <div class="js-errors"></div>
            `;

        // console.log('createSingleElements', this.key, value, dataType);

        const validateAll = (value): boolean => {
            if (value.toString() === '' || value === null) {
                this.setError(_('Enter a value'));
                return false;
            }
            return true;
        };

        let inputValue = FilterInputFactory(
            dataType,
            {
                className: 'js-value-from filter-input',
                value,
                placeholder: '',
                maxLength,
                db_autosuggest_column_name: key,
            },
            {
                onChange: value => {
                    this.callbacks.onChangeAnything(this.key);
                    if (validateAll(value) === false) {
                        return false;
                    }
                    if (!new Filter().isValueHasValidFormat(value, dataType)) {
                        this.setError(_('Invalid value format'));
                        return false;
                    }
                    this.setError();
                    this.values = new Filter()._getParsedValueIfNeeded(value, dataType);
                    return true;
                },
            }
        );

        let el = input_element.querySelector('.js-value-to-container') as HTMLDivElement;
        if (el) {
            el.appendChild(inputValue.element);
        }
        root_element.appendChild(input_element);

        this.values = new Filter()._getParsedValueIfNeeded(value, dataType);
        validateAll(value);
    }

    onClose(filter: Filter) {
        this.filterItem.mongoCondition = filter.getFilterItem(this.key.split('/')[0]);
        if (
            this.filterItem?.displayMethod == FilterConfig.displayMethod.RADIUS ||
            this.filterItem?.displayMethod == FilterConfig.displayMethod.AREA
        ) {
            if (
                this.filterItem?.mongoCondition?.properties?.filter ==
                this.filterItem?.displayMethod
            ) {
                SelectedArea.setGeometry(this.filterItem.mongoCondition);
                // SelectedArea.drawArea();
            } else {
                SelectedArea.resetGeometry();
            }
        }
    }

    setError(error: string | null = null) {
        let el_errors = this.root.querySelector('.js-errors') as HTMLDivElement;
        if (el_errors) {
            // console.log('setError, error', error);
            el_errors.innerHTML = error ?? '';
        } else {
            throw new Error('el_errors is null');
        }
        let btn_save = this.root.querySelector('.js-filter-save') as HTMLButtonElement;
        if (btn_save) {
            if (error === null || error === '') {
                // console.log('setError, btn_save, enable', this.root.querySelectorAll('.js-filter-save'));
                // btn_save.disabled = false;
                btn_save.removeAttribute('disabled');
            } else {
                // console.log('setError, btn_save, disable');
                // btn_save.disabled = true;
                btn_save.setAttribute('disabled', 'disabled');
            }
        } else {
            throw new Error('btn_save is null');
        }
    }
}

export default FilterEditorItem;
